import {
  CLAIM_CLOSURE_KIND_LABELS,
  analysisHoldLabel,
  analysisSituationLabel,
  claimPhaseLabel,
  compensationSituationLabel,
  peripheralPhaseLabel,
  peripheralSituationLabel,
  repairSituationLabel,
  type ClaimClosureKind,
  type ManagementLine,
  type ManagementMoveKind,
} from '@evogard/domain';
import type { ManagementClaim } from '@evogard/contract';

/** O nome de cada linha no mapa. */
// @valor — as chaves são os valores de `ManagementLine`
export const LINE_LABEL: Readonly<Record<ManagementLine, string>> = {
  analise: 'Análise',
  reparo: 'Reparo',
  indenizacao: 'Indenização',
  perifericos: 'Periféricos',
};

/** "da Análise", "do Reparo" — para a frase "vem da Análise". */
export const LINE_OF: Readonly<Record<ManagementLine, string>> = {
  analise: 'da Análise',
  reparo: 'do Reparo',
  indenizacao: 'da Indenização',
  perifericos: 'de Periféricos',
};

/** "a Análise", "o Reparo" — para a frase "vai para o Reparo". */
export const LINE_TO: Readonly<Record<ManagementLine, string>> = {
  analise: 'a Análise',
  reparo: 'o Reparo',
  indenizacao: 'a Indenização',
  perifericos: 'Periféricos',
};

/**
 * A cor de cada linha, da paleta de FASE (`--cor-fase-*`), e não de estado.
 *
 * O artefato pintava a Indenização de verde, e o verde da casa é estado — "está
 * certo, está pago" (decisão 1 do `DESIGN.md`). A paleta de fase existe para
 * decorar o que não afirma nada, e é a mesma das colunas dos quadros. As
 * classes vêm escritas por extenso porque o Tailwind só gera o que lê.
 */
export interface LineTone {
  stroke: string;
  border: string;
  fill: string;
  soft: string;
  /** A ponta da seta da estrada, que é preenchimento e não traço. */
  head: string;
}
export const LINE_TONE: Readonly<Record<ManagementLine, LineTone>> = {
  analise: {
    stroke: 'stroke-fase-laranja',
    border: 'border-fase-laranja',
    fill: 'bg-fase-laranja',
    soft: 'bg-fase-laranja-suave',
    head: 'fill-fase-laranja',
  },
  reparo: {
    stroke: 'stroke-fase-roxo',
    border: 'border-fase-roxo',
    fill: 'bg-fase-roxo',
    soft: 'bg-fase-roxo-suave',
    head: 'fill-fase-roxo',
  },
  indenizacao: {
    stroke: 'stroke-fase-turquesa',
    border: 'border-fase-turquesa',
    fill: 'bg-fase-turquesa',
    soft: 'bg-fase-turquesa-suave',
    head: 'fill-fase-turquesa',
  },
  perifericos: {
    stroke: 'stroke-fase-azul',
    border: 'border-fase-azul',
    fill: 'bg-fase-azul',
    soft: 'bg-fase-azul-suave',
    head: 'fill-fase-azul',
  },
};

/** O nome da etapa, pelo catálogo da linha. */
export function stageLabel(line: ManagementLine, phase: string): string {
  return line === 'perifericos'
    ? peripheralPhaseLabel(phase)
    : claimPhaseLabel(line, phase);
}

/** O nome da situação, pelo catálogo da linha. */
export function situationLabel(line: ManagementLine, code: string): string {
  if (line === 'analise') return analysisSituationLabel(code);
  if (line === 'reparo') return repairSituationLabel(code);
  if (line === 'indenizacao') return compensationSituationLabel(code);
  return peripheralSituationLabel(code);
}

/** O motivo de o acionamento estar travado: a parada, ou a situação que trava. */
export function stallLabel(
  line: ManagementLine,
  stall: { kind: 'parada' | 'situacao'; code: string },
): string {
  return stall.kind === 'parada'
    ? analysisHoldLabel(stall.code)
    : situationLabel(line, stall.code);
}

/** O número do acionamento como a casa o escreve. */
export function numberOf(c: Pick<ManagementClaim, 'source' | 'id'>): string {
  return c.source === 'periferico' ? `PER #${c.id}` : `#${c.id}`;
}

/** As iniciais da bolinha — duas letras, do primeiro e do último nome. */
export function initialsOf(name: string | null): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]![0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]![0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** Dias inteiros desde a entrada na fase; `null` quando a entrada não está gravada. */
export function daysSince(iso: string | null, now = Date.now()): number | null {
  if (!iso) return null;
  return Math.max(0, Math.floor((now - new Date(iso).getTime()) / 86_400_000));
}

/** "entrou hoje", "há 1 dia", "há 12 dias". */
export function ageText(days: number | null): string {
  if (days === null) return 'entrada na fase não registrada';
  if (days === 0) return 'entrou hoje';
  return days === 1 ? 'há 1 dia' : `há ${days} dias`;
}

/** O verbo de cada rota, na dica da estação. */
// @valor — as chaves são os valores de `ManagementMoveKind`
export const MOVE_LABEL: Readonly<Record<ManagementMoveKind, string>> = {
  avanca: 'Avança para',
  volta: 'Volta para',
  desvia: 'Desvia para',
  retorna: 'Retorna à avenida em',
  envia: 'Envia para',
  encerra: 'Encerra em',
};

/** O traço de cada rota, quando o mapa a desenha. */
// @valor — as chaves são os valores de `ManagementMoveKind`
export const MOVE_STROKE: Readonly<Record<ManagementMoveKind, string>> = {
  avanca: 'stroke-fase-verde',
  volta: 'stroke-texto-tenue',
  desvia: 'stroke-alerta',
  retorna: 'stroke-fase-verde',
  envia: 'stroke-fase-verde',
  encerra: 'stroke-texto-suave',
};

/** O nome do encerramento, no ✓ ou ✕ ao lado da estação. */
export function endLabel(kind: ClaimClosureKind): string {
  return CLAIM_CLOSURE_KIND_LABELS[kind];
}
